import React from 'react';
import { Box, Typography, Button, Tooltip, useTheme, alpha } from '@mui/material';
import {
  ArrowForward as ArrowIcon, CheckCircleOutline as DoneIcon, EventBusy as MissingIcon,
  ErrorOutline as OverdueIcon, Schedule as DueIcon, AccessTime as ClockIcon,
  ChecklistRtl as TodoIcon, AssignmentOutlined as TasksIcon, FolderOpen as ProjectIcon, Timeline as TrendIcon,
} from '@mui/icons-material';
import StatusPill from '../StatusPill';
import { Ring, TrendArea, HBars } from './Charts';
import { SoftHero, SoftCard, SoftTitle, Pill, EmptyState } from './Soft';
import { captionSx, fmtHours, dayLabel, COLORS, periodWord, tint, scanTooltip } from './dashboardUtils';

export const Empty = ({ children }) => (
  <Typography sx={{ ...captionSx, py: 2 }}>{children}</Typography>
);

const weekLabel = (iso) => dayLabel(iso, { day: 'numeric', month: 'short' });
const DAY_MS = 86400000;
const daysBetween = (a, b) => Math.round((new Date(`${b}T00:00:00`) - new Date(`${a}T00:00:00`)) / DAY_MS);

const DAY_FILL = { logged: 'mint', over: 'peach', missing: 'rose', pending: 'sky', future: 'slate' };
const DAY_WORD = { logged: 'logged', over: 'over scan', missing: 'missing', pending: 'today', future: '' };

/** The week as five rounded day tiles. Colour says the state; the number is the hours. */
export const DayTiles = ({ days, onPick, size = 'lg' }) => {
  const theme = useTheme();
  const many = days.length > 7;
  const big = size === 'lg' && !many;
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: `repeat(${many ? 'auto-fill' : days.length}, minmax(${many ? '44px' : '0'}, 1fr))`, gap: many ? 1.5 : 2.5 }}>
      {days.map((d) => {
        const t = tint(theme, DAY_FILL[d.status] || 'slate');
        const faded = d.status === 'future';
        const hollow = d.status === 'pending';
        const clickable = d.status === 'missing' && typeof onPick === 'function';
        return (
          <Tooltip key={d.date} title={`${dayLabel(d.date, { weekday: 'long', day: 'numeric', month: 'short' })} · ${scanTooltip(d)}`} arrow>
            <Box
              onClick={clickable ? () => onPick(d.date) : undefined}
              sx={{
                borderRadius: big ? 4 : 2.5, px: big ? 3 : 1, py: big ? 3 : 1.5, textAlign: 'center', minWidth: 0,
                bgcolor: hollow ? (theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.04)' : alpha('#fff', 0.7)) : t.bg,
                border: hollow ? `2px dashed ${t.ink}` : '2px solid transparent',
                opacity: faded ? 0.55 : 1,
                cursor: clickable ? 'pointer' : 'default',
                transition: 'transform 0.15s',
                '&:hover': clickable ? { transform: 'translateY(-2px)' } : {},
              }}
            >
              <Typography noWrap sx={{ fontSize: big ? '0.75rem' : '0.62rem', fontWeight: 700, color: t.ink, textTransform: 'uppercase', letterSpacing: '0.04em' }}>
                {many ? dayLabel(d.date, { day: 'numeric' }) : dayLabel(d.date, { weekday: 'short' })}
              </Typography>
              <Typography sx={{ fontSize: big ? '1.75rem' : '1rem', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.15, mt: big ? 1 : 0.5, color: 'text.primary' }}>
                {d.status === 'future' ? '·' : d.status === 'missing' ? '0' : fmtHours(d.hours)}
              </Typography>
              {big && <Typography noWrap sx={{ fontSize: '0.72rem', color: t.ink, fontWeight: 600, mt: 0.5, minHeight: 16 }}>{DAY_WORD[d.status] || ''}</Typography>}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
};

/** One row in the to-do list: icon chip, text, sub-text, action. */
const TodoRow = ({ icon: Icon, fill, title, sub, action, onAction, last, extra }) => {
  const theme = useTheme();
  const t = tint(theme, fill);
  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: '40px 1fr auto', gap: 3, alignItems: 'center', py: 3, borderBottom: last ? 'none' : `1px solid ${theme.palette.divider}` }}>
      <Box sx={{ width: 40, height: 40, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: t.bg, color: t.ink }}>
        <Icon sx={{ fontSize: 20 }} />
      </Box>
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>{title}</Typography>
        {sub && <Typography noWrap sx={{ ...captionSx, fontSize: '0.78rem' }}>{sub}</Typography>}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {extra}
        {action && <Button size="small" variant="contained" disableElevation onClick={onAction} sx={{ fontWeight: 700, borderRadius: 999, px: 3, bgcolor: t.ink, '&:hover': { bgcolor: t.ink, filter: 'brightness(0.92)' } }}>{action}</Button>}
      </Box>
    </Box>
  );
};

/**
 * The team member's home: today's status and the Log button in the greeting
 * panel with the week as day tiles, then a to-do list, open tasks, and the
 * small charts in the rail.
 */
const MyWeek = ({ overview, header, onLogTime, onViewTasks, compact = false, reports = null }) => {
  const theme = useTheme();
  const { me, hoursPerDay, period, today, workingDays } = overview;
  const isToday = period === 'today';
  const word = periodWord(period);
  const expected = isToday ? hoursPerDay : me.expectedHours;
  const hours = isToday ? me.loggedToday : me.hours;
  const ratio = expected ? hours / expected : 0;
  const todayRow = me.days.find((d) => d.date === today);
  const isWorkingDay = workingDays.includes(today);
  const trend = me.trend.map((w) => ({ ...w, label: weekLabel(w.weekStart) }));
  const avgWeek = trend.length > 1 ? trend.slice(0, -1).reduce((t, w) => t + w.hours, 0) / (trend.length - 1) : 0;

  const missingDays = me.days.filter((d) => d.status === 'missing');
  const overdue = me.openTasks.filter((t) => t.overdue);
  const dueSoon = me.openTasks.filter((t) => !t.overdue && t.deadline && daysBetween(today, t.deadline) <= 7);
  const todoCount = missingDays.length + overdue.length + dueSoon.length;
  const loggedToday = me.loggedToday > 0;
  const statusFill = loggedToday ? 'mint' : isWorkingDay ? 'sky' : 'slate';
  const st = tint(theme, statusFill);
  const ringColor = me.missingDays > 0 ? COLORS.bad : theme.palette.primary.main;

  const logButton = (
    <Button variant="contained" disableElevation onClick={onLogTime} sx={{ fontWeight: 700, borderRadius: 999, px: 5, py: 2, fontSize: '0.9375rem', bgcolor: 'text.primary', color: 'background.paper', '&:hover': { bgcolor: 'text.primary', filter: 'brightness(1.15)' } }}>
      Log time
    </Button>
  );

  // Managers get the team as the page; their own week is one soft strip.
  if (compact) {
    return (
      <SoftCard sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '220px 1fr auto' }, gap: { xs: 4, md: 6 }, alignItems: 'center' }}>
        <Box>
          <Typography sx={{ ...captionSx, fontSize: '0.78rem', fontWeight: 600 }}>Your hours {word}</Typography>
          <Typography sx={{ fontSize: '1.75rem', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.15, mt: 0.5 }}>
            {fmtHours(hours)}<Box component="span" sx={{ fontSize: '0.9375rem', fontWeight: 500, color: 'text.secondary', ml: 1 }}>of {fmtHours(expected)} h</Box>
          </Typography>
          <Box sx={{ mt: 1.5 }}>
            {me.missingDays > 0
              ? <Pill fill="rose">{me.missingDays} day{me.missingDays === 1 ? '' : 's'} without hours</Pill>
              : loggedToday ? <Pill fill="mint">Logged today</Pill> : isWorkingDay ? <Pill fill="sky">Nothing logged yet today</Pill> : <Pill fill="mint">All caught up</Pill>}
          </Box>
        </Box>
        {me.days.length > 0 && <DayTiles days={me.days} onPick={() => onLogTime()} size="sm" />}
        {logButton}
      </SoftCard>
    );
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
      <SoftHero title={header?.title} subtitle={header?.subtitle} actions={header?.actions}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 4, flexWrap: 'wrap', mb: 5 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
            <Box sx={{ width: 52, height: 52, borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center', bgcolor: theme.palette.mode === 'dark' ? st.bg : '#fff', color: st.ink, flexShrink: 0 }}>
              {loggedToday ? <DoneIcon sx={{ fontSize: 28 }} /> : <ClockIcon sx={{ fontSize: 28 }} />}
            </Box>
            <Box>
              <Typography sx={{ fontSize: '1.25rem', fontWeight: 800, letterSpacing: '-0.02em' }}>
                {loggedToday ? `${fmtHours(me.loggedToday)} h logged today` : isWorkingDay ? 'Nothing logged yet today' : 'No hours due today'}
              </Typography>
              <Typography sx={{ ...captionSx, fontSize: '0.875rem' }}>
                {todayRow?.scannedHours != null ? `In since ${todayRow.firstIn}` : dayLabel(today, { weekday: 'long' })}
                {isWorkingDay && !loggedToday && <> · {hoursPerDay} h expected</>}
                {me.missingDays > 0 && <> · <Box component="span" sx={{ color: COLORS.bad, fontWeight: 700 }}>{me.missingDays} earlier day{me.missingDays === 1 ? '' : 's'} missing</Box></>}
              </Typography>
            </Box>
          </Box>
          {logButton}
        </Box>
        {me.days.length > 0 && <DayTiles days={me.days} onPick={() => onLogTime()} />}
      </SoftHero>

      {/* Widget grid: 12 columns, rows share their height. */}
      <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: 'repeat(12, minmax(0, 1fr))' }, gap: 4, alignItems: 'stretch', '& > *': { minWidth: 0 } }}>
        {/* Row: to do | hours ring */}
        <Box sx={{ gridColumn: { lg: 'span 8' } }}>
          <SoftCard sx={{ height: '100%' }}>
            <SoftTitle icon={TodoIcon} fill="peach" title="To do" sub={todoCount === 0 ? 'Nothing waiting on you' : `${todoCount} thing${todoCount === 1 ? '' : 's'} to take care of`} />
            {todoCount === 0 && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 3, py: 2 }}>
                <DoneIcon sx={{ color: COLORS.good, fontSize: 28 }} />
                <Typography sx={{ fontSize: '0.9375rem' }}>You're caught up. Every working day has hours and nothing is due this week.</Typography>
              </Box>
            )}
            {missingDays.map((d, i) => (
              <TodoRow
                key={d.date} icon={MissingIcon} fill="rose"
                title={`Log hours for ${dayLabel(d.date, { weekday: 'long', day: 'numeric', month: 'short' })}`}
                sub={d.scannedHours != null ? `You were in ${d.firstIn} – ${d.lastOut} (${fmtHours(d.scannedHours)} h scanned)` : 'No hours logged'}
                action="Log" onAction={onLogTime}
                last={i === missingDays.length - 1 && overdue.length === 0 && dueSoon.length === 0}
              />
            ))}
            {overdue.map((t, i) => (
              <TodoRow
                key={t.id} icon={OverdueIcon} fill="rose"
                title={t.title} sub={`${t.projectName} · was due ${dayLabel(t.deadline, { day: 'numeric', month: 'short' })}`}
                action="Open" onAction={onViewTasks}
                last={i === overdue.length - 1 && dueSoon.length === 0}
              />
            ))}
            {dueSoon.map((t, i) => (
              <TodoRow
                key={t.id} icon={DueIcon} fill="lemon"
                title={t.title} sub={`${t.projectName} · due ${dayLabel(t.deadline, { weekday: 'short', day: 'numeric', month: 'short' })}`}
                action="Open" onAction={onViewTasks}
                last={i === dueSoon.length - 1}
              />
            ))}
          </SoftCard>
        </Box>

        <Box sx={{ gridColumn: { lg: 'span 4' } }}>
          <SoftCard fill="sky" sx={{ height: '100%', display: 'flex', alignItems: 'center', gap: 5 }}>
            <Ring value={ratio} color={ringColor} size={104} stroke={10} track={alpha(ringColor, 0.15)}>
              <Typography sx={{ fontSize: '1.375rem', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1 }}>{Math.round(ratio * 100)}%</Typography>
            </Ring>
            <Box>
              <Typography sx={{ fontSize: '0.8125rem', fontWeight: 600, color: 'text.secondary' }}>Hours {word}</Typography>
              <Typography sx={{ fontSize: '1.75rem', fontWeight: 800, letterSpacing: '-0.02em', lineHeight: 1.15, mt: 0.5 }}>
                {fmtHours(hours)}<Box component="span" sx={{ fontSize: '0.9375rem', fontWeight: 500, color: 'text.secondary', ml: 1 }}>of {fmtHours(expected)} h</Box>
              </Typography>
              <Typography sx={{ ...captionSx, fontSize: '0.78rem', mt: 0.5 }}>
                {me.daysDue > 0 ? `${me.daysDue - me.missingDays} of ${me.daysDue} days logged` : 'no working day finished yet'}
              </Typography>
            </Box>
          </SoftCard>
        </Box>

        {/* Row: open tasks | projects + trend */}
        <Box sx={{ gridColumn: { lg: 'span 7' } }}>
          <SoftCard sx={{ height: '100%' }}>
            <SoftTitle
              icon={TasksIcon} fill="lavender" title="Open tasks" sub={`${me.openTaskCount} assigned to you`}
              right={me.openTaskCount > 0 && <Button size="small" endIcon={<ArrowIcon />} onClick={onViewTasks} sx={{ fontWeight: 700, borderRadius: 999 }}>All tasks</Button>}
            />
            {me.openTasks.length === 0
              ? <EmptyState icon={TasksIcon} fill="lavender" title="Nothing assigned to you" sub="Tasks your manager assigns to you appear here." />
              : me.openTasks.slice(0, 8).map((t, i) => (
                <Box key={t.id} sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 3, py: 2.5, borderBottom: i === Math.min(me.openTasks.length, 8) - 1 ? 'none' : `1px solid ${theme.palette.divider}` }}>
                  <Box sx={{ minWidth: 0 }}>
                    <Typography noWrap sx={{ fontSize: '0.9375rem', fontWeight: 600 }}>{t.title}</Typography>
                    <Typography noWrap sx={{ ...captionSx, fontSize: '0.78rem' }}>{t.projectName}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexShrink: 0 }}>
                    {t.deadline && (
                      <Pill fill={t.overdue ? 'rose' : 'slate'} dot={false}>{t.overdue ? 'overdue' : 'due'} {dayLabel(t.deadline, { day: 'numeric', month: 'short' })}</Pill>
                    )}
                    <StatusPill status={t.status} />
                  </Box>
                </Box>
              ))}
          </SoftCard>
        </Box>

        <Box sx={{ gridColumn: { lg: 'span 5' }, display: 'flex', flexDirection: 'column', gap: 4 }}>
          <SoftCard>
            <SoftTitle icon={ProjectIcon} fill="mint" title="Hours by project" />
            {me.hoursByProject.length === 0
              ? <EmptyState icon={ProjectIcon} fill="mint" title={`No hours logged ${word} yet`} sub="Log time and your projects show up here." />
              : (
                <HBars
                  rows={me.hoursByProject.slice(0, 6)}
                  labelOf={(r) => r.projectKey || r.name}
                  valueOf={(r) => r.hours}
                  valueLabel={(r) => `${fmtHours(r.hours)} h`}
                  colorOf={(r) => (r.isBillable === false ? COLORS.muted : theme.palette.primary.main)}
                />
              )}
          </SoftCard>

          <SoftCard sx={{ flex: 1 }}>
            <SoftTitle icon={TrendIcon} fill="lavender" title="Hours per week" sub={`Average ${fmtHours(avgWeek)} h over the last ${Math.max(1, trend.length - 1)} weeks`} />
            <TrendArea data={trend} dataKey="hours" color={theme.palette.primary.main} height={110} formatter={(w) => `${fmtHours(w.hours)} h · ${w.daysLogged}/${w.daysDue} days`} />
          </SoftCard>
        </Box>

        {/* Row: reports, full width */}
        <Box sx={{ gridColumn: { lg: 'span 12' } }}>{reports}</Box>
      </Box>
    </Box>
  );
};

export default MyWeek;
